import { computed, ref, watch, onMounted, toValue, type Ref, type MaybeRefOrGetter, type VNodeRef } from 'vue'

import { unrefElement } from './unrefElement'

import type {
  MaybeComputedElementRef,
  MaybeElement,
} from '@/composables/unrefElement'

export interface ResizeObserverSize {
  readonly inlineSize: number
  readonly blockSize: number
}

export interface ResizeObserverEntry {
  readonly target: Element
  readonly contentRect: DOMRectReadOnly
  readonly borderBoxSize: ReadonlyArray<ResizeObserverSize>
  readonly contentBoxSize: ReadonlyArray<ResizeObserverSize>
  readonly devicePixelContentBoxSize: ReadonlyArray<ResizeObserverSize>
}

export type ResizeObserverCallback = (
  entries: ReadonlyArray<ResizeObserverEntry>,
  observer: ResizeObserver
) => void

export interface UseResizeObserverOptions {
  box?: ResizeObserverBoxOptions
}
type UseResizeObserverReturn = {
  isSupported: Ref<boolean>
  stop: () => void
}
type UseResizeObserver = (
  target:
    | VNodeRef
    | MaybeComputedElementRef
    | MaybeComputedElementRef[]
    | MaybeRefOrGetter<MaybeElement[]>,
  callback: ResizeObserverCallback,
  options?: UseResizeObserverOptions
) => UseResizeObserverReturn

declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback)
  disconnect(): void
  observe(target: Element, options?: UseResizeObserverOptions): void
  unobserve(target: Element): void
}

export const useResizeObserver: UseResizeObserver = (
  target,
  callback,
  options = {},
) => {
  const { ...observerOptions } = options

  let observer: ResizeObserver | undefined
  const isSupported = ref<boolean>(false)

  const cleanup = () => {
    if (observer) {
      observer.disconnect()
      observer = undefined
    }
  }

  const targets = computed(() => {
    const _targets = toValue(target)
    return Array.isArray(_targets)
      ? _targets.map(el => unrefElement(el))
      : [unrefElement(_targets)]
  })

  const stopWatch = watch(
    targets,
    (els) => {
      cleanup()
      if (isSupported.value && window) {
        observer = new ResizeObserver(callback)
        for (const _el of els) {
          if (_el) {
            observer!.observe(_el, observerOptions)
          }
        }
      }
    },
    { immediate: true, flush: 'post' },
  )

  const stop = () => {
    cleanup()
    stopWatch()
  }

  onMounted(() => {
    isSupported.value = window && 'ResizeObserver' in window
  })

  return {
    isSupported,
    stop,
  }
}
